/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useState} from 'react';
import {ColDef} from 'ag-grid-community';
import {GenericObject} from '@ion/core/src/types/entities';

/**
 * @useColumnChooser
 *
 * Provides a Hook which allows for managing the display state and current column selections
 * state of an IonGrid. The state returned by this hook is to be passed to an IonGrid instance,
 * and optionally used for persisting the current column selections (either to the application
 * store, externally to CTC, or both).
 *
 * ```typescript
 *
 *     const {
 *       showColumnChooser,
 *       columnDefinitions,
 *       onColumnChooserSave,
 *       toggleColumnChooser
 *     } = useColumnChooser();
 *
 *     ...
 *
 *    <IonGrid ...
 *      showColumnChooser={showColumnChooser}
 *      onColumnChooserSave={onColumnChooserSave}
 *      onColumnChooserClose={function() {
 *        toggleColumnChooser(false);
 *      }}
 *      columnDefinitions={{
 *        desktop : getColumnDefinitionsDesktop(columnDefinitions),
 *         mobile : getColumnDefinitionsMobile(columnDefinitions)
 *      }}
 *      ... />
 *
 * ```
 */
export const useColumnChooser = (definitions: ColDef[], resolver?: Function, resolverOptions?: GenericObject) => {
  const columns = resolver ? resolver(definitions, resolverOptions): definitions;
  const [showColumnChooser, setShowColumnChooser] = useState<boolean>(false);
  const [columnDefinitions, setColumnDefinitions] = useState<ColDef[] | null>(columns);

  useEffect(() => {
    setColumnDefinitions(columns);
  }, [definitions, resolverOptions]);

  /**
   * @private
   */
  const onColumnChooserSave = function(columns: ColDef[]): void {
    setColumnDefinitions(columns);
  };

  /**
   * @private
   */
  const toggleColumnChooser = function(isOpen = false): void {
    setShowColumnChooser(isOpen);
  };

  return {
    showColumnChooser,
    columnDefinitions,
    onColumnChooserSave,
    toggleColumnChooser
  };
};

export default useColumnChooser;
